/**
 * Date utilities for consistent timezone handling across the app.
 * All dates are displayed in US Eastern timezone.
 */

const TIMEZONE = 'America/New_York';

/**
 * Format a date for display (date only, no time)
 * @param date - Date string, Date object, or timestamp
 * @param options - Optional Intl.DateTimeFormatOptions to override defaults
 * @returns Formatted date string in Eastern timezone
 */
export function formatDate(
  date: string | Date | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  };

  return d.toLocaleDateString('en-US', defaultOptions);
}

/**
 * Format a date with time
 * @param date - Date string, Date object, or timestamp
 * @param options - Optional Intl.DateTimeFormatOptions to override defaults
 * @returns Formatted datetime string in Eastern timezone
 */
export function formatDateTime(
  date: string | Date | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  return d.toLocaleString('en-US', defaultOptions);
}

/**
 * Format date as short month and day (e.g., "Jan 15")
 */
export function formatShortDate(date: string | Date | number | null | undefined): string {
  return formatDate(date, { month: 'short', day: 'numeric', year: undefined });
}

/**
 * Format date as month and 2-digit year (e.g., "Jan 25")
 */
export function formatMonthYear(date: string | Date | number | null | undefined): string {
  return formatDate(date, { month: 'short', year: '2-digit', day: undefined });
}

/**
 * Format a month string like "2025-01" to "Jan 25"
 */
export function formatMonthString(monthStr: string): string {
  if (!monthStr || !monthStr.match(/^\d{4}-\d{2}$/)) return monthStr;
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return formatMonthYear(date);
}

/**
 * Format a day string like "2025-01-15" to "Jan 15"
 */
export function formatDayString(dayStr: string): string {
  if (!dayStr || !dayStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dayStr;
  const [year, month, day] = dayStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return formatShortDate(date);
}

/**
 * Get the current date/time in Eastern timezone
 */
export function getEasternNow(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: TIMEZONE }));
}

/**
 * Check if a date is today in Eastern timezone
 */
export function isToday(date: string | Date | number): boolean {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = getEasternNow();
  const dateInEastern = new Date(d.toLocaleString('en-US', { timeZone: TIMEZONE }));

  return (
    dateInEastern.getFullYear() === now.getFullYear() &&
    dateInEastern.getMonth() === now.getMonth() &&
    dateInEastern.getDate() === now.getDate()
  );
}

/**
 * Format date for form inputs (YYYY-MM-DD)
 * Adjusts for timezone to ensure the displayed date matches Eastern time
 */
export function formatDateForInput(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  // Get the date components in Eastern timezone
  const parts = d.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).split('/');

  // Convert from MM/DD/YYYY to YYYY-MM-DD
  return `${parts[2]}-${parts[0]}-${parts[1]}`;
}

/**
 * Bounds for a date-of-birth `<input type="date">`.
 *
 * A segmented date input accepts a two-digit year literally, so typing 5/5/68
 * yields "0068-05-05" — a valid date the backend's 18+ gate happily reads as an
 * age of 1958. Six prod patients were stored that way before these bounds
 * existed. `min` is the one that catches it; the backend enforces the same
 * range in utils/dateOfBirthValidation.ts.
 */
export const MAX_AGE_YEARS = 120;

export function dobInputBounds(): { min: string; max: string } {
  const today = new Date();
  const earliest = new Date(today);
  earliest.setFullYear(earliest.getFullYear() - MAX_AGE_YEARS);
  return {
    min: formatDateForInput(earliest),
    max: formatDateForInput(today)
  };
}

/**
 * Range-check a DOB from a date input. Returns an error message, or null.
 *
 * The `min`/`max` attributes stop the picker from producing an out-of-range
 * value, but a form can still be submitted with one typed in — Safari and
 * Firefox do not block submit on a range violation the way they block a
 * malformed value. Mirrors backend utils/dateOfBirthValidation.ts.
 */
export function dobRangeError(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return 'Please enter a valid date of birth.';
  if (date.getTime() > Date.now()) return 'Date of birth cannot be in the future.';

  const now = new Date();
  let age = now.getUTCFullYear() - date.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - date.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < date.getUTCDate())) age--;

  if (age > MAX_AGE_YEARS) {
    return `That birth year (${date.getUTCFullYear()}) looks wrong. Enter the full four-digit year, e.g. 1968 rather than 68.`;
  }
  return null;
}
